import { useCallback, useRef } from "react";

const GRAPHICS_COMMAND_RE = /\\(?:includegraphics|includesvg)(?:\s*\[[^\]]*])?\s*\{([^{}]+)\}/g;
const GRAPHICS_FILE_EXTENSIONS = new Set(["gif", "jpeg", "jpg", "pdf", "png", "svg", "webp"]);

export function normalizeGraphicsPath(path) {
    return String(path || "")
        .trim()
        .replace(/^['"]|['"]$/g, "")
        .replace(/^\.?\//, "")
        .replace(/^\/+/, "");
}

export function isExternalGraphicsPath(path) {
    return /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(path || "");
}

function pathWithoutKnownGraphicsExtension(path) {
    const normalized = normalizeGraphicsPath(path);
    const extension = normalized.includes(".") ? normalized.split(".").pop().toLowerCase() : "";

    if (!GRAPHICS_FILE_EXTENSIONS.has(extension)) {
        return normalized;
    }
    return normalized.slice(0, -(extension.length + 1));
}

function collectProjectGraphicsPaths(content = "") {
    const paths = new Set();

    for (const match of String(content).matchAll(GRAPHICS_COMMAND_RE)) {
        const path = normalizeGraphicsPath(match[1]);
        if (!path || isExternalGraphicsPath(path)) {
            continue;
        }
        paths.add(path);
    }

    return [...paths];
}

function collectBinaryGraphicsPathKeys(nodes) {
    const keys = new Set();

    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        if (node?.node_type !== "binary") {
            return;
        }

        const path = normalizeGraphicsPath(node?.path_cache || node?.name);
        if (!path) {
            return;
        }

        keys.add(path);
        keys.add(pathWithoutKnownGraphicsExtension(path));
    });

    return keys;
}

export function findMissingProjectGraphicsPaths(content, treeNodes) {
    const knownPaths = collectBinaryGraphicsPathKeys(treeNodes);

    return collectProjectGraphicsPaths(content).filter((path) => {
        const normalizedPath = normalizeGraphicsPath(path);
        return !knownPaths.has(normalizedPath) && !knownPaths.has(pathWithoutKnownGraphicsExtension(normalizedPath));
    });
}

export function resolveFigurePreviewNode(nodes, path) {
    const normalizedPath = normalizeGraphicsPath(path);
    const normalizedWithoutExtension = pathWithoutKnownGraphicsExtension(normalizedPath);

    return (Array.isArray(nodes) ? nodes : []).find((node) => {
        if (node?.node_type !== "binary") return false;
        const candidatePath = normalizeGraphicsPath(node?.path_cache || node?.name || "");
        return (
            candidatePath === normalizedPath ||
            pathWithoutKnownGraphicsExtension(candidatePath) === normalizedWithoutExtension
        );
    });
}

export function useMissingGraphicsTreeRefetch({ treeNodes, refetchTree }) {
    const refetchSignatureRef = useRef("");

    return useCallback(
        (document) => {
            const missingPaths = findMissingProjectGraphicsPaths(document?.current_content || "", treeNodes);
            if (!missingPaths.length) {
                return;
            }

            const signature = `${document?.node_id || "unknown"}:${missingPaths.sort().join("|")}`;
            if (refetchSignatureRef.current === signature) {
                return;
            }
            refetchSignatureRef.current = signature;

            window.setTimeout(() => {
                void refetchTree?.();
            }, 100);
            window.setTimeout(() => {
                void refetchTree?.();
            }, 1000);
        },
        [refetchTree, treeNodes],
    );
}
